'use client';

import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { FormAutocomplete, FormSelect, FormTextField } from '@/components/common/FormComponents';
import { CollaboratorDto, CollaboratorHistorySliceDto, PrivilegeEnum, RoleDto } from '@/api/ApiClient';
import { usePrivileges } from '@/hooks/usePrivileges';
import { useAuthStore } from '@/stores/authStore';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { nullIfBlank } from '@/lib/utils/nullIfBlank';
import { OptionKey, type OptionMap } from '@/lib/options/optionTypes';
import { US_JURISDICTION_OPTIONS } from '@/lib/constants/usJurisdictionOptions';
import { useBackend } from '@/hooks/useBackend';
import { useSnackbar } from '@/hooks/useSnackbar';
import { invalidateCachedOptions } from '@/lib/options/optionsCache';
import { buildCollaboratorRequest, type UserCollaboratorDialogRecord } from '@/lib/users/collaboratorRequestForm';
import { initEppnFormRows, type EppnFormRow } from '@/lib/users/collaboratorEppnForm';
import {
  formatHistorySliceSummary,
  isCurrentOpenTarget,
  isOpenTimeslice,
  profileFieldsFromHistorySlice,
  timesliceKey,
  todayInputDate,
  type TimesliceEditTarget,
} from '@/lib/users/collaboratorTimesliceForm';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { buildTechniqueOptions } from '@/lib/utils/optionUtils';

const OPTION_KEYS: OptionKey[] = ['roles', 'facultyOptions', 'synthesisTechniquesLeafOnly', 'synthesisInstruments'];

const MAX_LEN = 30;

function isAssignableUserRole(role: RoleDto): boolean {
  return role.id > 0 && role.isUser !== false;
}

function inferIsInhouseFromRecord(record: UserCollaboratorDialogRecord | null): boolean {
  if (!record) {
    return true;
  }
  const r0 = record.roles?.[0];
  if (typeof r0?.isInhouse === 'boolean') {
    return r0.isInhouse;
  }
  if (record.userType === 'external') {
    return false;
  }
  if (record.userType === 'internal') {
    return true;
  }
  return true;
}

/** Create flow: selected role must match In-house vs External (same rule as `roleOptions` filtering). */
function isCreateModeRoleAllowedForUserType(
  roles: RoleDto[] | undefined,
  roleId: number | '',
  userKind: 'internal' | 'external'
): boolean {
  if (roleId === '' || roleId === undefined) {
    return true;
  }
  const match = roles?.find((r) => r.id === roleId);
  if (!match) {
    return false;
  }
  return userKind === 'internal' ? Boolean(match.isInhouse) : !match.isInhouse;
}

function buildInstrumentOptionsForTechniques(rawInstruments: any[] | undefined, selectedTechniqueIds: string[]) {
  if (!rawInstruments || selectedTechniqueIds.length === 0) {
    return [];
  }
  const selected = new Set(selectedTechniqueIds.map((id) => String(id)));
  return rawInstruments
    .filter((inst) => inst.id !== undefined && inst.name !== undefined)
    .filter((inst) => (inst.synthesisTechniques ?? []).some((techId: string) => selected.has(String(techId))))
    .map((inst) => ({
      value: inst.id ?? '',
      label: `${inst.id ?? ''} - ${inst.name ?? 'Unknown Instrument'}`,
    }));
}

/** Drop managed instrument IDs that are not valid for the given technique selection (same rules as the instrument picker). */
function pruneManagedInstrumentsForTechniques(
  rawInstruments: any[] | undefined,
  selectedTechniqueIds: string[],
  managedInstrumentIds: string[]
): string[] {
  if (!managedInstrumentIds.length) return managedInstrumentIds;
  const allowed = new Set(
    buildInstrumentOptionsForTechniques(rawInstruments, selectedTechniqueIds).map((o) => String(o.value))
  );
  const next = managedInstrumentIds.filter((id) => allowed.has(String(id)));
  return next.length === managedInstrumentIds.length ? managedInstrumentIds : next;
}

function normalizeZipForForm(zip: unknown): string {
  if (zip === null || zip === undefined) {
    return '';
  }
  return String(zip);
}

function isInSameResearchGroup(
  currentUser: { id?: string; faculty?: string } | null | undefined,
  recordFaculty: string | undefined
): boolean {
  if (!currentUser?.id || !recordFaculty) {
    return false;
  }
  return (
    recordFaculty === currentUser.id ||
    recordFaculty === currentUser.faculty ||
    recordFaculty === (currentUser.faculty || currentUser.id)
  );
}

function isResearcherRole(roles: RoleDto[] | undefined, roleId: number | ''): boolean {
  if (roleId === '' || roleId === undefined) {
    return false;
  }
  return Boolean(roles?.find((r) => r.id === roleId)?.isResearcher);
}

/**
 * Optional create-flow constraints when opened from a project field (PI / faculty / staff).
 * Limits user type and/or which roles can be chosen so the new user fits the target dropdown.
 */
export type UserCollaboratorCreatePreset = {
  /** Initial In-house vs External selection. */
  userKind?: 'internal' | 'external';
  /** When true, the user-type radio is disabled. */
  lockUserKind?: boolean;
  /** Extra role filter after assignable + in-house/external filters. */
  rolePredicate?: (role: RoleDto) => boolean;
  /** Short helper under User type (e.g. why only faculty roles are listed). */
  helperText?: string;
  /** Prefill first name when creating from a picker (ignored when `isEdit`). */
  initialFirstName?: string;
  /** Prefill last name when creating from a picker (ignored when `isEdit`). */
  initialLastName?: string;
  /** Prefill email when creating from a picker (ignored when `isEdit`). */
  initialEmail?: string;
};

export interface UserCollaboratorDialogProps {
  open: boolean;
  onClose: () => void;
  isEdit: boolean;
  /** API / grid row; omit for create. */
  record?: UserCollaboratorDialogRecord | null;
  /** Loaded by parent before opening edit (customizing users grid). Omit for create. */
  timesliceHistory?: CollaboratorHistorySliceDto[];
  /** Called after a successful create/update; caller may update local UI. Cache invalidation runs after close. */
  onSave: (saved: CollaboratorDto) => Promise<void>;
  /** Create-only constraints (ignored when `isEdit`). */
  createPreset?: UserCollaboratorCreatePreset;
}

function initRoleIdFromRecord(record: UserCollaboratorDialogRecord | null | undefined, isEdit: boolean): number | '' {
  if (!isEdit || !record) return '';
  const rid = Array.isArray(record.roleIds) ? record.roleIds[0] : record.roleIds;
  return typeof rid === 'number' ? rid : rid !== undefined && rid !== '' ? Number(rid) : '';
}

/** Dedicated create/edit UI for collaborators; calls `user.create` / `user.update` with generated request DTOs. */
function UserCollaboratorDialogInner({
  open,
  onClose,
  isEdit,
  record,
  timesliceHistory,
  onSave,
  createPreset,
  options,
  optionsLoading,
}: UserCollaboratorDialogProps & {
  options: Partial<
    Pick<
      OptionMap,
      'roles' | 'facultyOptions' | 'synthesisTechniques' | 'synthesisInstruments' | 'synthesisTechniquesLeafOnly'
    >
  >;
  optionsLoading: boolean;
}) {
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const { isAuthorized } = usePrivileges();
  const { currentUser } = useAuthStore();

  const hasUserUpdate = isAuthorized(PrivilegeEnum.User, 'update');
  const hasUserCreate = isAuthorized(PrivilegeEnum.User, 'create');
  const hasUserList = isAuthorized(PrivilegeEnum.User, 'list');
  const hasResearchGroupUpdate = isAuthorized(PrivilegeEnum.ResearchGroup, 'update');
  const hasResearchGroupCreate = isAuthorized(PrivilegeEnum.ResearchGroup, 'create');

  // Create + research-group-only: force in-house under the current user's faculty (dialog remounts via key).
  const researchGroupCreatePreset = !isEdit && !hasUserUpdate && (hasResearchGroupCreate || hasResearchGroupUpdate);
  const presetFaculty = currentUser?.faculty || currentUser?.id;

  const [userKind, setUserKind] = useState<'internal' | 'external'>(() => {
    if (record && isEdit) {
      return inferIsInhouseFromRecord(record) ? 'internal' : 'external';
    }
    return createPreset?.userKind ?? 'internal';
  });
  const [activeTab, setActiveTab] = useState<'profile' | 'signin'>('profile');
  const [eppnRows, setEppnRows] = useState<EppnFormRow[]>(() =>
    initEppnFormRows({
      eppns: record?.eppns,
      isEdit: Boolean(record && isEdit),
    })
  );
  const [firstName, setFirstName] = useState(() =>
    record && isEdit ? String(record.firstName ?? '') : String(createPreset?.initialFirstName ?? '')
  );
  const [middleName, setMiddleName] = useState(() => (record && isEdit ? String(record.middleName ?? '') : ''));
  const [lastName, setLastName] = useState(() =>
    record && isEdit ? String(record.lastName ?? '') : String(createPreset?.initialLastName ?? '')
  );
  const [abbreviation, setAbbreviation] = useState(() => (record && isEdit ? String(record.abbreviation ?? '') : ''));
  const [orcId, setOrcId] = useState(() => (record && isEdit ? String(record.orcId ?? '') : ''));
  const [faculty, setFaculty] = useState<string | undefined>(() => {
    if (record && isEdit) {
      return nullIfBlank(String(record.faculty ?? ''));
    }
    if (researchGroupCreatePreset && presetFaculty) {
      return presetFaculty;
    }
    return undefined;
  });
  const [roleId, setRoleId] = useState<number | ''>(() => initRoleIdFromRecord(record, isEdit));
  const [techniques, setTechniques] = useState<string[]>(() =>
    record && isEdit && Array.isArray(record.techniques) ? [...record.techniques] : []
  );
  const [managedInstruments, setManagedInstruments] = useState<string[]>(() =>
    record && isEdit && Array.isArray(record.managedInstruments) ? [...record.managedInstruments] : []
  );
  const [position, setPosition] = useState(() => (record && isEdit ? String(record.position ?? '') : ''));
  const [institution, setInstitution] = useState(() => (record && isEdit ? String(record.institution ?? '') : ''));
  const [city, setCity] = useState(() => (record && isEdit ? String(record.city ?? '') : ''));
  const [stateValue, setStateValue] = useState(() => (record && isEdit ? String(record.state ?? '') : ''));
  const [zip, setZip] = useState(() => (record && isEdit ? normalizeZipForForm(record.zip) : ''));
  const [country, setCountry] = useState(() => (record && isEdit ? String(record.country ?? 'USA') : 'USA'));
  const [email, setEmail] = useState(() =>
    record && isEdit ? String(record.email ?? '') : String(createPreset?.initialEmail ?? '')
  );
  const [phone, setPhone] = useState(() => (record && isEdit ? String(record.phone ?? '') : ''));
  const [isAdmin, setIsAdmin] = useState(() => (record && isEdit ? Boolean(record.isAdmin) : false));
  const [inactive, setInactive] = useState(() => (record && isEdit ? Boolean(record.inactive) : false));
  const [teamsUserId, setTeamsUserId] = useState(() => (record && isEdit ? String(record.teamsUserId ?? '') : ''));

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [editTarget, setEditTarget] = useState<TimesliceEditTarget>({ kind: 'current-open' });
  const [effectiveFromInput, setEffectiveFromInput] = useState(todayInputDate);

  const editingHistoricalSlice =
    isEdit && editTarget.kind === 'historical' && !isCurrentOpenTarget(editTarget, record ?? undefined);
  const editingCurrentOpenSlice = isEdit && isCurrentOpenTarget(editTarget, record ?? undefined);

  const applyProfileFields = (fields: ReturnType<typeof profileFieldsFromHistorySlice>) => {
    setFirstName(fields.firstName);
    setMiddleName(fields.middleName);
    setLastName(fields.lastName);
    setEmail(fields.email);
    setPhone(fields.phone);
    setPosition(fields.position);
    setInstitution(fields.institution);
    setCity(fields.city);
    setStateValue(fields.state);
    setZip(normalizeZipForForm(fields.zip));
    setCountry(fields.country || 'USA');
    setFaculty(fields.faculty);
  };

  const loadCurrentSliceForEdit = () => {
    if (!record) {
      return;
    }
    setEditTarget({ kind: 'current-open' });
    setEffectiveFromInput(todayInputDate());
    applyProfileFields({
      firstName: String(record.firstName ?? ''),
      middleName: String(record.middleName ?? ''),
      lastName: String(record.lastName ?? ''),
      email: String(record.email ?? ''),
      phone: String(record.phone ?? ''),
      position: String(record.position ?? ''),
      institution: String(record.institution ?? ''),
      city: String(record.city ?? ''),
      state: String(record.state ?? ''),
      zip: normalizeZipForForm(record.zip),
      country: String(record.country ?? 'USA'),
      faculty: record.faculty ? nullIfBlank(String(record.faculty)) : undefined,
    });
    setAbbreviation(String(record.abbreviation ?? ''));
    setOrcId(String(record.orcId ?? ''));
  };

  const loadHistoricalSliceForEdit = (slice: CollaboratorHistorySliceDto) => {
    setEditTarget({
      kind: 'historical',
      validFrom: slice.validFrom ?? '',
      validTo: slice.validTo ?? '',
    });
    setEffectiveFromInput('');
    applyProfileFields(profileFieldsFromHistorySlice(slice));
  };

  const isSliceLoadedForEdit = (slice: CollaboratorHistorySliceDto): boolean => {
    if (editTarget.kind === 'current-open') {
      return timesliceKey(slice.validFrom, slice.validTo) === timesliceKey(record?.validFrom, record?.validTo);
    }
    return editTarget.validFrom === slice.validFrom && editTarget.validTo === slice.validTo;
  };

  const isInhouseUser = isEdit ? inferIsInhouseFromRecord(record ?? null) : userKind === 'internal';

  /** KO StaffVM: ResearchGroup without User — edit in-house researchers in own faculty group. */
  const researchGroupEditable = useMemo(() => {
    if (hasUserUpdate) {
      return false;
    }
    if (!isEdit) {
      return hasResearchGroupCreate || hasResearchGroupUpdate;
    }
    if (!record || !inferIsInhouseFromRecord(record)) {
      return false;
    }
    if (!hasResearchGroupUpdate) {
      return false;
    }
    const rid = initRoleIdFromRecord(record, true);
    if (!isResearcherRole(options.roles, rid)) {
      return false;
    }
    return isInSameResearchGroup(currentUser, record.faculty ? String(record.faculty) : undefined);
  }, [hasUserUpdate, hasResearchGroupUpdate, hasResearchGroupCreate, isEdit, record, options.roles, currentUser]);

  const canSave = useMemo(() => {
    if (hasUserUpdate) {
      return true;
    }
    if (!isEdit) {
      return hasUserCreate || (researchGroupEditable && hasResearchGroupCreate);
    }
    return researchGroupEditable;
  }, [hasUserUpdate, isEdit, hasUserCreate, researchGroupEditable, hasResearchGroupCreate]);

  /** Sign-in (EPPN) tab: full user admins, or research-group editors adding in-house students. */
  const showSignInTab = hasUserList || (researchGroupEditable && isInhouseUser);
  const researchGroupCreateOnly = !isEdit && researchGroupEditable && !hasUserCreate;
  // Don't sync tab into state when sign-in disappears — clamp during render.
  const visibleTab = showSignInTab ? activeTab : 'profile';

  const roleOptions = useMemo(() => {
    const roles = options.roles ?? [];
    const presetPredicate = !isEdit ? createPreset?.rolePredicate : undefined;
    return roles
      .filter((r) => isAssignableUserRole(r))
      .filter((r) => (isInhouseUser ? r.isInhouse : !r.isInhouse))
      .filter((r) => !researchGroupEditable || r.isResearcher)
      .filter((r) => !presetPredicate || presetPredicate(r))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map((r) => ({ value: r.id, label: r.name || String(r.id) }));
  }, [options.roles, isInhouseUser, researchGroupEditable, isEdit, createPreset?.rolePredicate]);

  /**
   * Research-group mode unlocks in-house student onboarding fields (name, role, techniques,
   * managed instruments, contact, sign-in). Faculty and admin-only fields stay locked.
   */
  const fieldDisabled = (allowInResearchGroup = false) => {
    if (optionsLoading || saving) {
      return true;
    }
    if (hasUserUpdate || (!isEdit && hasUserCreate)) {
      return false;
    }
    if (researchGroupEditable) {
      return !allowInResearchGroup;
    }
    return true;
  };

  const cantDeactivate =
    (!hasUserUpdate && !researchGroupEditable) || Boolean(isEdit && record?.id && record.id === currentUser?.id);

  const facultyOptions = useMemo(() => {
    const raw = options.facultyOptions ?? [];
    return raw.map((o) => ({ value: o.key, label: o.value }));
  }, [options.facultyOptions]);

  const techniqueOptions = useMemo(
    () => buildTechniqueOptions(options.synthesisTechniquesLeafOnly).map((t) => ({ value: t.id, label: t.name })),
    [options.synthesisTechniquesLeafOnly]
  );

  const instrumentOptions = useMemo(
    () => buildInstrumentOptionsForTechniques(options.synthesisInstruments as any[], techniques),
    [options.synthesisInstruments, techniques]
  );
  const managedInstrumentsLocked = techniques.length === 0;

  const usStateSelectOptions = useMemo(
    () =>
      US_JURISDICTION_OPTIONS.map((j) => ({
        value: j.code,
        label: `${j.label} (${j.code})`,
      })),
    []
  );

  const countryTrimmed = country.trim();
  /** Empty country is treated as USA (UI + API); same rule as legacy behavior. */
  const countryEffectiveIsUsa = !countryTrimmed || countryTrimmed.toUpperCase() === 'USA';
  const useUsStateDropdown = countryEffectiveIsUsa;
  /** External users: state + zip are required only for USA (including blank country → USA). */
  const requireUsStateAndZip = !isInhouseUser && countryEffectiveIsUsa;

  /** Create mode: if roles refresh and the stored id is gone, coerce to empty for the Select without an effect. */
  const roleIdForFormSelect = useMemo(() => {
    if (roleId === '' || roleId === undefined) return roleId;
    if (isEdit) return roleId;
    return options.roles?.some((r) => r.id === roleId) ? roleId : '';
  }, [roleId, isEdit, options.roles]);

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!firstName.trim()) {
      next.firstName = 'First name is required';
    }
    if (!lastName.trim()) {
      next.lastName = 'Last name is required';
    }
    if (roleIdForFormSelect === '' || roleIdForFormSelect === undefined) {
      next.roleId = 'Role is required';
    }
    if (orcId.length > 20) {
      next.orcId = 'ORCID must be at most 20 characters';
    }

    if (!isInhouseUser) {
      if (!position.trim()) {
        next.position = 'Position is required';
      }
      if (!institution.trim()) {
        next.institution = 'Institution is required';
      }
      if (!city.trim()) {
        next.city = 'City is required';
      }

      if (countryEffectiveIsUsa) {
        if (!stateValue.trim()) {
          next.state = 'State is required';
        }
        if (!zip.trim()) {
          next.zip = 'Zip / Postal code is required';
        }
      }
    }

    if (zip.length > MAX_LEN) {
      next.zip = `Zip must be at most ${MAX_LEN} characters`;
    }
    if (countryTrimmed.length > MAX_LEN) {
      next.country = `Country must be at most ${MAX_LEN} characters`;
    }

    // Sign-in identifiers: no duplicate EPPNs (case-insensitive).
    const seenEppns = new Set<string>();
    eppnRows.forEach((row, index) => {
      const trimmed = row.eppn.trim();
      if (!trimmed) {
        return;
      }
      const key = trimmed.toLowerCase();
      if (seenEppns.has(key)) {
        next[`eppn-${index}`] = 'Duplicate EPPN';
      } else {
        seenEppns.add(key);
      }
    });

    if (editingCurrentOpenSlice && !effectiveFromInput.trim()) {
      next.effectiveFrom = 'Effective date is required';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }
    if (roleIdForFormSelect === '' || roleIdForFormSelect === undefined) {
      return;
    }

    setSaving(true);
    try {
      const req = buildCollaboratorRequest({
        isEdit,
        isInhouseUser,
        record: record ?? null,
        editTarget,
        effectiveFromInput,
        form: {
          eppnRows,
          firstName,
          middleName,
          lastName,
          abbreviation,
          orcId,
          faculty,
          roleId: roleIdForFormSelect,
          techniques,
          managedInstruments,
          position,
          institution,
          city,
          state: stateValue,
          zip,
          country,
          email,
          phone,
          isAdmin,
          inactive,
          teamsUserId,
        },
      });

      let saved: CollaboratorDto;
      if (isEdit && record?.id) {
        saved = await backend.user.update(record.id, req);
      } else {
        saved = await backend.user.create(req);
      }

      // Close first so parents that refresh option caches (invalidate users/staff) do not
      // unmount this dialog mid-save and leave a stuck MUI backdrop.
      await onSave(saved);
      showSnackbar('User saved successfully', 'success');
      onClose();
      invalidateCachedOptions(['users', 'staff']);
    } catch (error) {
      showSnackbar(getProblemDetail(error) ?? 'Failed to save user.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const savingOrLoading = optionsLoading || saving;

  return (
    <Dialog
      open={open}
      onClose={savingOrLoading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'min(90vh, 900px)',
        },
      }}
    >
      <DialogTitle sx={{ flexShrink: 0 }}>{isEdit ? 'Edit user' : 'Create user'}</DialogTitle>
      <Box sx={{ flexShrink: 0, px: 3 }}>
        <Tabs
          value={visibleTab}
          onChange={(_, value: 'profile' | 'signin') => setActiveTab(value)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Profile" value="profile" />
          {showSignInTab ? <Tab label="Sign-in" value="signin" /> : null}
        </Tabs>
      </Box>
      <DialogContent dividers sx={{ flex: 1, minHeight: 0, overflow: 'auto', pt: 2 }}>
        <Box sx={{ display: visibleTab === 'profile' ? 'flex' : 'none', flexDirection: 'column', gap: 1.5 }}>
          {isEdit && editingHistoricalSlice ? (
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                bgcolor: 'action.hover',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="body2">
                Editing closed timeslice {editTarget.kind === 'historical' ? editTarget.validFrom : ''} –{' '}
                {editTarget.kind === 'historical' ? editTarget.validTo : ''}
              </Typography>
              <Button size="small" onClick={loadCurrentSliceForEdit} disabled={savingOrLoading}>
                Back to current
              </Button>
            </Box>
          ) : null}

          {editingCurrentOpenSlice ? (
            <FormTextField
              label="New slice effective from"
              type="date"
              required
              value={effectiveFromInput}
              onChange={(e) => setEffectiveFromInput(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={Boolean(errors.effectiveFrom)}
              helperText={
                errors.effectiveFrom ?? 'When profile or affiliation changes, start the new timeslice on this date.'
              }
              disabled={fieldDisabled(false)}
            />
          ) : null}
          <Box sx={{ width: '100%' }}>
            {isEdit ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  User type: {isInhouseUser ? 'In-house' : 'External'} (from role; not editable)
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 2,
                    width: '100%',
                  }}
                >
                  {isInhouseUser ? (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isAdmin}
                          onChange={(e) => setIsAdmin(e.target.checked)}
                          disabled={!hasUserUpdate || savingOrLoading}
                        />
                      }
                      label="Admin"
                      sx={{ mr: 0 }}
                    />
                  ) : null}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={inactive}
                        onChange={(e) => setInactive(e.target.checked)}
                        disabled={cantDeactivate || savingOrLoading}
                      />
                    }
                    label="Inactive"
                    sx={{ mr: 0 }}
                  />
                </Box>
              </>
            ) : researchGroupCreateOnly ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Creating an in-house researcher for your research group.
              </Typography>
            ) : (
              <>
                <Typography variant="subtitle2" component="div" sx={{ mb: 0.5, lineHeight: 1.3 }}>
                  User type
                </Typography>
                {createPreset?.helperText ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {createPreset.helperText}
                  </Typography>
                ) : null}
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    columnGap: 2,
                    rowGap: 1,
                    width: '100%',
                  }}
                >
                  <RadioGroup
                    row
                    value={userKind}
                    onChange={(_, v) => {
                      if (createPreset?.lockUserKind) {
                        return;
                      }
                      const nextKind = v === 'external' ? 'external' : 'internal';
                      setUserKind(nextKind);
                      setErrors({});
                      if (!isEdit) {
                        setRoleId((rid) =>
                          isCreateModeRoleAllowedForUserType(options.roles, rid, nextKind) ? rid : ''
                        );
                      }
                    }}
                    sx={{
                      alignItems: 'center',
                      flex: '1 1 auto',
                      minWidth: 0,
                      gap: 0.5,
                    }}
                  >
                    <FormControlLabel
                      value="internal"
                      control={<Radio />}
                      label="In-house"
                      disabled={Boolean(createPreset?.lockUserKind) || savingOrLoading}
                      sx={{ mr: 0 }}
                    />
                    <FormControlLabel
                      value="external"
                      control={<Radio />}
                      label="External"
                      disabled={Boolean(createPreset?.lockUserKind) || savingOrLoading}
                      sx={{ mr: 0 }}
                    />
                  </RadioGroup>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 2,
                      flex: '0 0 auto',
                    }}
                  >
                    {isInhouseUser ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isAdmin}
                            onChange={(e) => setIsAdmin(e.target.checked)}
                            disabled={!hasUserUpdate || savingOrLoading}
                          />
                        }
                        label="Admin"
                        sx={{ mr: 0 }}
                      />
                    ) : null}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={inactive}
                          onChange={(e) => setInactive(e.target.checked)}
                          disabled={cantDeactivate || savingOrLoading}
                        />
                      }
                      label="Inactive"
                      sx={{ mr: 0 }}
                    />
                  </Box>
                </Box>
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <FormTextField
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                error={!!errors.firstName}
                helperText={errors.firstName}
                fullWidth
                disabled={fieldDisabled(true)}
              />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <FormTextField
                label="Middle name"
                value={middleName}
                onChange={(e) => setMiddleName(e.target.value)}
                fullWidth
                disabled={fieldDisabled(true)}
              />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <FormTextField
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                error={!!errors.lastName}
                helperText={errors.lastName}
                fullWidth
                disabled={fieldDisabled(true)}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <FormTextField
                label="Abbreviation"
                value={abbreviation}
                onChange={(e) => setAbbreviation(e.target.value)}
                fullWidth
                disabled={fieldDisabled(true)}
              />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <FormTextField
                label="ORCID"
                value={orcId}
                onChange={(e) => setOrcId(e.target.value)}
                error={!!errors.orcId}
                helperText={errors.orcId}
                fullWidth
                disabled={fieldDisabled(true)}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <FormSelect
                label="Role"
                value={roleIdForFormSelect === '' ? '' : roleIdForFormSelect}
                onChange={(e) => {
                  const v = e.target.value;
                  setRoleId(v === '' ? '' : Number(v));
                }}
                options={roleOptions}
                required
                error={!!errors.roleId}
                helperText={errors.roleId}
                disabled={fieldDisabled(true)}
              />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <FormTextField
                label="Position"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                required={!isInhouseUser}
                error={!!errors.position}
                helperText={errors.position}
                fullWidth
                disabled={fieldDisabled(true)}
              />
            </Box>
          </Box>

          {isInhouseUser ? (
            <>
              <FormAutocomplete
                options={facultyOptions}
                value={faculty ? facultyOptions.find((o) => o.value === faculty) || null : null}
                onChange={(_e, newValue) => setFaculty(nullIfBlank(newValue?.value))}
                getOptionLabel={(option: any) => option.label || ''}
                isOptionEqualToValue={(option: any, value: any) => option.value === value.value}
                label="Faculty"
                disabled={fieldDisabled(false)}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                  <FormAutocomplete
                    multiple
                    options={techniqueOptions}
                    value={techniqueOptions.filter((o) => techniques.includes(String(o.value)))}
                    onChange={(_e, newValue) => {
                      const nextTechniques = newValue.map((item: any) => String(item.value));
                      setTechniques(nextTechniques);
                      setManagedInstruments((prev) =>
                        pruneManagedInstrumentsForTechniques(
                          options.synthesisInstruments as any[],
                          nextTechniques,
                          prev
                        )
                      );
                    }}
                    getOptionLabel={(option: any) => option.label || ''}
                    isOptionEqualToValue={(option: any, value: any) => option.value === value.value}
                    label="Synthesis techniques"
                    disabled={fieldDisabled(true)}
                  />
                </Box>
                <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                  <FormAutocomplete
                    multiple
                    options={instrumentOptions}
                    value={instrumentOptions.filter((o) => managedInstruments.includes(String(o.value)))}
                    onChange={(_e, newValue) => setManagedInstruments(newValue.map((item: any) => String(item.value)))}
                    getOptionLabel={(option: any) => option.label || ''}
                    isOptionEqualToValue={(option: any, value: any) => option.value === value.value}
                    label="Managed instruments"
                    disabled={fieldDisabled(true) || managedInstrumentsLocked}
                    helperText={managedInstrumentsLocked ? 'Select synthesis techniques first.' : undefined}
                  />
                </Box>
              </Box>
            </>
          ) : null}

          {!isInhouseUser ? (
            <>
              <FormTextField
                label="Institution"
                value={institution}
                onChange={(e) => setInstitution(e.target.value)}
                required
                error={!!errors.institution}
                helperText={errors.institution}
                fullWidth
              />

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                  <FormTextField
                    label="City"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    required
                    error={!!errors.city}
                    helperText={errors.city}
                    fullWidth
                  />
                </Box>
                <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                  <FormTextField
                    label="Zip / Postal code"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    required={requireUsStateAndZip}
                    fullWidth
                    inputProps={{ maxLength: MAX_LEN }}
                    error={!!errors.zip}
                    helperText={errors.zip}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                  {useUsStateDropdown ? (
                    <FormSelect
                      label="State"
                      value={stateValue}
                      onChange={(e) => setStateValue(String(e.target.value))}
                      options={usStateSelectOptions}
                      required={requireUsStateAndZip}
                      error={!!errors.state}
                      helperText={errors.state}
                      disabled={fieldDisabled(false)}
                    />
                  ) : (
                    <FormTextField
                      label="State / Province"
                      value={stateValue}
                      onChange={(e) => setStateValue(e.target.value)}
                      required={requireUsStateAndZip}
                      error={!!errors.state}
                      helperText={errors.state}
                      fullWidth
                      disabled={fieldDisabled(false)}
                    />
                  )}
                </Box>
                <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                  <FormTextField
                    label="Country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    fullWidth
                    inputProps={{ maxLength: MAX_LEN }}
                    error={!!errors.country}
                    helperText={errors.country}
                    disabled={fieldDisabled(true)}
                  />
                </Box>
              </Box>
            </>
          ) : null}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <FormTextField
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                error={!!errors.email}
                helperText={errors.email}
                disabled={fieldDisabled(true)}
              />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <FormTextField
                label="Phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
                disabled={fieldDisabled(true)}
              />
            </Box>
          </Box>

          {isEdit && timesliceHistory !== undefined ? (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Profile history
              </Typography>
              {timesliceHistory.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No timeslices found.
                </Typography>
              ) : (
                <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
                  {timesliceHistory.map((slice) => {
                    const facultyLabel = slice.faculty
                      ? facultyOptions.find((o) => o.value === slice.faculty)?.label
                      : undefined;
                    const loaded = isSliceLoadedForEdit(slice);
                    const open = isOpenTimeslice(slice.validTo);
                    return (
                      <Box
                        component="li"
                        key={timesliceKey(slice.validFrom, slice.validTo)}
                        sx={{
                          mb: 1,
                          p: 1,
                          borderRadius: 1,
                          border: 1,
                          borderColor: loaded ? 'primary.main' : 'divider',
                          bgcolor: loaded ? 'action.selected' : 'transparent',
                        }}
                      >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
                          <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={loaded ? 600 : 400}>
                              {slice.validFrom} – {slice.validTo}
                              {open ? ' (current)' : ''}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatHistorySliceSummary(slice, facultyLabel)}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant={loaded ? 'contained' : 'outlined'}
                            onClick={() =>
                              timesliceKey(slice.validFrom, slice.validTo) ===
                              timesliceKey(record?.validFrom, record?.validTo)
                                ? loadCurrentSliceForEdit()
                                : loadHistoricalSliceForEdit(slice)
                            }
                            disabled={hasUserUpdate ? savingOrLoading : true}
                          >
                            {loaded ? 'Editing' : 'Edit slice'}
                          </Button>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          ) : null}
        </Box>

        <Box sx={{ display: visibleTab === 'signin' ? 'flex' : 'none', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            EPPNs used for sign-in (Shibboleth / Azure AD). Saving replaces the full list for this user.
          </Typography>
          {eppnRows.map((row, index) => (
            <Box key={row.key} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ flex: '2 1 240px', minWidth: 0 }}>
                <FormTextField
                  label="EPPN"
                  value={row.eppn}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEppnRows((rows) => rows.map((r) => (r.key === row.key ? { ...r, eppn: value } : r)));
                  }}
                  fullWidth
                  error={!!errors[`eppn-${index}`]}
                  helperText={errors[`eppn-${index}`]}
                  disabled={fieldDisabled(true)}
                />
              </Box>
              <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
                <FormTextField
                  label="Expires"
                  type="date"
                  value={row.expiresAt}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEppnRows((rows) => rows.map((r) => (r.key === row.key ? { ...r, expiresAt: value } : r)));
                  }}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  disabled={fieldDisabled(true)}
                />
              </Box>
              <IconButton
                aria-label="Remove EPPN"
                onClick={() => setEppnRows((rows) => rows.filter((r) => r.key !== row.key))}
                disabled={fieldDisabled(true) || eppnRows.length <= 1}
                sx={{ mt: 0.5 }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={() => setEppnRows((rows) => [...rows, { key: `new-${Date.now()}`, eppn: '', expiresAt: '' }])}
            disabled={fieldDisabled(true)}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add EPPN
          </Button>
          <FormTextField
            label="Microsoft Teams user ID"
            value={teamsUserId}
            onChange={(e) => setTeamsUserId(e.target.value)}
            fullWidth
            disabled={!hasUserUpdate || savingOrLoading}
            helperText="Azure AD object ID for @mentions in Teams shipping notifications. Not used for sign-in."
            sx={{ mt: 1 }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={!canSave || savingOrLoading}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function UserCollaboratorDialog(props: UserCollaboratorDialogProps) {
  const { options, loading: optionsLoading } = useSelectOptions(OPTION_KEYS);
  if (!props.open) return null;
  const createKey = props.isEdit
    ? String(props.record?.id ?? 'edit')
    : `create:${props.createPreset?.userKind ?? 'any'}:${props.createPreset?.lockUserKind ? 'locked' : 'free'}:${props.createPreset?.initialFirstName ?? ''}:${props.createPreset?.initialLastName ?? ''}:${props.createPreset?.initialEmail ?? ''}`;
  return <UserCollaboratorDialogInner key={createKey} {...props} options={options} optionsLoading={optionsLoading} />;
}
